import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBook } from '../context/BookContext';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Separator } from '../components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Calendar } from '../components/ui/calendar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import { bookingApi, paymentApi, loyaltyApi } from '../lib/api';
import InsuranceRecommendation from '../components/InsuranceRecommendation';
import {
  BookOpen, Trash2, MessageSquare, Calendar as CalendarIcon, Clock,
  MapPin, DollarSign, CheckCircle, AlertCircle, Flame, ExternalLink,
  ChevronRight, CreditCard, FileText, Shield, X, Users, Loader2, Wallet, Gift, Star
} from 'lucide-react';
import { format } from 'date-fns';

// PayPal icon component
const PayPalIcon = () => (
  <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor">
    <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944 3.72a.641.641 0 0 1 .632-.544h6.964c2.327 0 4.14.629 5.392 1.872.596.589 1.017 1.274 1.255 2.04.254.815.313 1.749.177 2.776-.019.132-.041.265-.067.4-.452 2.433-1.517 4.246-3.177 5.396-1.562 1.084-3.558 1.634-5.932 1.634h-1.2a.641.641 0 0 0-.633.544l-.848 5.499zm6.112-15.917H8.676l-1.18 7.645h2.513c1.79 0 3.247-.359 4.335-1.068 1.144-.747 1.845-1.867 2.082-3.331.24-1.553-.107-2.666-.892-3.347-.682-.592-1.787-.899-3.346-.899z"/>
  </svg>
);

// Platform fee and tax rates
const PLATFORM_FEE_PERCENT = 0.08;
const VENUE_FEE_PERCENT = 0.10;  // 10% venue fee when venue is involved
const TAX_RATE = 0.08875;
const INSURANCE_FEE_PERCENT = 0.05;

const MyBook = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { 
    bookItems, 
    removeFromBook, 
    updateServiceDetails, 
    confirmBookItem,
    clearBook,
    getTotalEstimatedPrice,
    getCheckoutReadyItems
  } = useBook();

  const [selectedItem, setSelectedItem] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [checkoutMode, setCheckoutMode] = useState('combined'); // combined or separate
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutDialogOpen, setCheckoutDialogOpen] = useState(false);
  const [createdBookings, setCreatedBookings] = useState(null);
  
  // Payment state
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('stripe');
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);
  
  // Per-item options (insurance, contract request, loyalty discount)
  const [itemOptions, setItemOptions] = useState({}); // { itemId: { insurance: bool, requestContract: bool, applyLoyaltyDiscount: bool } }
  
  // Insurance acknowledgment state
  const [insuranceAcknowledged, setInsuranceAcknowledged] = useState(false);
  
  // Loyalty discounts state - available discounts from API
  const [loyaltyDiscounts, setLoyaltyDiscounts] = useState({}); // { talentId: { discount_percent, booking_count, tier } }

  useEffect(() => {
    loadPaymentMethods();
  }, []);
  
  // Load loyalty discounts when book items change
  useEffect(() => {
    if (bookItems.length > 0 && user) {
      loadLoyaltyDiscounts();
    }
  }, [bookItems, user]);
  
  const loadLoyaltyDiscounts = async () => {
    const uniqueTalentIds = [...new Set(bookItems.map(item => item.bookee?.user_id).filter(Boolean))];
    
    const discountPromises = uniqueTalentIds.map(async (talentId) => {
      try {
        const response = await loyaltyApi.getStatus(talentId);
        return { talentId, discount: response.data };
      } catch (error) {
        return { talentId, discount: null };
      }
    });
    
    const results = await Promise.all(discountPromises);
    const discountsMap = {};
    results.forEach(({ talentId, discount }) => {
      if (discount && discount.discount_percent > 0) {
        discountsMap[talentId] = discount;
      }
    });
    setLoyaltyDiscounts(discountsMap);
    
    // Auto-enable loyalty discount for eligible items (user can toggle off)
    const newItemOptions = { ...itemOptions };
    bookItems.forEach(item => {
      const talentId = item.bookee?.user_id;
      if (discountsMap[talentId] && newItemOptions[item.id]?.applyLoyaltyDiscount === undefined) {
        newItemOptions[item.id] = {
          ...newItemOptions[item.id],
          applyLoyaltyDiscount: true // Default to applying discount
        };
      }
    });
    setItemOptions(newItemOptions);
  };

  const loadPaymentMethods = async () => {
    try {
      const response = await paymentApi.getPaymentMethods();
      setPaymentMethods(response.data.payment_methods || []);
      // Default to first available method
      if (response.data.payment_methods?.length > 0) {
        setSelectedPaymentMethod(response.data.payment_methods[0].id);
      }
    } catch (error) {
      console.error('Failed to load payment methods:', error);
    }
  };

  const toggleItemOption = (itemId, option) => {
    setItemOptions(prev => ({
      ...prev,
      [itemId]: {
        ...prev[itemId],
        [option]: !prev[itemId]?.[option]
      }
    }));
  };

  // Status display helper
  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending_message':
        return <Badge variant="outline" className="text-yellow-500 border-yellow-500">Message to Confirm</Badge>;
      case 'messaged':
        return <Badge variant="outline" className="text-blue-500 border-blue-500">Awaiting Response</Badge>;
      case 'confirmed':
        return <Badge className="bg-green-500">Confirmed</Badge>;
      default:
        return <Badge variant="secondary">Pending</Badge>;
    }
  };

  const handleEditItem = (item) => {
    setSelectedItem({ ...item });
    setEditDialogOpen(true);
  };

  const handleSaveDetails = () => {
    if (selectedItem) {
      updateServiceDetails(selectedItem.id, selectedItem.serviceDetails);
      toast.success('Details updated');
      setEditDialogOpen(false);
    }
  };

  const handleMessageBookee = (item) => {
    // Navigate to messages with this bookee
    navigate(`/messages?to=${item.bookee.user_id}`);
  };

  // Calculate price breakdown for an item
  const calculateItemPrice = (item) => {
    const originalPrice = item.serviceDetails.estimatedPrice || item.bookee.hourly_rate || 50;
    
    // Check if loyalty discount is available AND user wants to apply it
    const talentId = item.bookee?.user_id;
    const loyaltyDiscount = loyaltyDiscounts[talentId];
    const applyDiscount = itemOptions[item.id]?.applyLoyaltyDiscount && loyaltyDiscount;
    
    const discountPercent = applyDiscount ? (loyaltyDiscount?.discount_percent || 0) : 0;
    const discountAmount = (originalPrice * discountPercent) / 100;
    const basePrice = originalPrice - discountAmount;
    
    const insurance = itemOptions[item.id]?.insurance ? basePrice * INSURANCE_FEE_PERCENT : 0;
    const platformFee = basePrice * PLATFORM_FEE_PERCENT;
    // Check if a venue is selected for this booking
    const hasVenue = item.serviceDetails?.venueId || item.serviceDetails?.venue_id;
    const venueFee = hasVenue ? basePrice * VENUE_FEE_PERCENT : 0;
    const taxableAmount = basePrice + platformFee;
    const tax = taxableAmount * TAX_RATE;
    const total = basePrice + platformFee + venueFee + insurance + tax;
    
    return {
      originalPrice: Math.round(originalPrice * 100) / 100,
      discountPercent: discountPercent,
      discountAmount: Math.round(discountAmount * 100) / 100,
      loyaltyTier: loyaltyDiscount?.tier || null,
      loyaltyAvailable: !!loyaltyDiscount,
      availableDiscountPercent: loyaltyDiscount?.discount_percent || 0,
      discountApplied: applyDiscount,
      basePrice: Math.round(basePrice * 100) / 100,
      platformFee: Math.round(platformFee * 100) / 100,
      venueFee: Math.round(venueFee * 100) / 100,
      insurance: Math.round(insurance * 100) / 100,
      tax: Math.round(tax * 100) / 100,
      total: Math.round(total * 100) / 100,
      hasVenue: !!hasVenue
    };
  };

  // Calculate total for all checkout items
  const calculateCheckoutTotal = () => {
    const readyItems = getCheckoutReadyItems();
    let total = 0;
    readyItems.forEach(item => {
      const prices = calculateItemPrice(item);
      total += prices.total;
    });
    return Math.round(total * 100) / 100;
  };

  const handleProceedToCheckout = async () => {
    const readyItems = getCheckoutReadyItems();
    if (readyItems.length === 0) {
      toast.error('Please confirm at least one booking before checkout');
      return;
    }

    // Validate that all ready items have required details
    const incompleteItems = readyItems.filter(item => 
      !item.serviceDetails.date || !item.serviceDetails.startTime
    );
    
    if (incompleteItems.length > 0) {
      toast.error(`Please set date and time for all confirmed items (${incompleteItems.length} incomplete)`);
      return;
    }

    // Open payment dialog for combined checkout
    if (checkoutMode === 'combined') {
      setPaymentDialogOpen(true);
    } else {
      // For separate mode, create bookings and redirect to bookings page
      await createBookingsAndRedirect();
    }
  };

  const createBookingsAndRedirect = async () => {
    const readyItems = getCheckoutReadyItems();
    setCheckoutLoading(true);
    try {
      const batchData = {
        items: readyItems.map(item => ({
          talent_id: item.bookee.user_id,
          date: item.serviceDetails.date,
          start_time: item.serviceDetails.startTime,
          end_time: item.serviceDetails.endTime || null,
          event_type: item.serviceDetails.eventType || null,
          event_location: item.serviceDetails.eventLocation || null,
          notes: item.serviceDetails.notes || null,
          service_category: item.bookee.main_category || item.bookee.categories?.[0] || null,
          pricing_type: item.serviceDetails.pricing_type || 'hourly',
          custom_amount: item.serviceDetails.custom_amount || null,
          add_insurance: itemOptions[item.id]?.insurance || false
        })),
        checkout_mode: 'separate'
      };

      await bookingApi.createBatch(batchData);
      clearBook();
      toast.success('Bookings created! Pay for each booking individually.');
      navigate('/bookings');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create bookings');
    } finally {
      setCheckoutLoading(false);
    }
  };

  const handleCombinedPayment = async () => {
    const readyItems = getCheckoutReadyItems();
    setPaymentLoading(true);
    
    try {
      // First create all bookings
      const batchData = {
        items: readyItems.map(item => ({
          talent_id: item.bookee.user_id,
          date: item.serviceDetails.date,
          start_time: item.serviceDetails.startTime,
          end_time: item.serviceDetails.endTime || null,
          event_type: item.serviceDetails.eventType || null,
          event_location: item.serviceDetails.eventLocation || null,
          notes: item.serviceDetails.notes || null,
          service_category: item.bookee.main_category || item.bookee.categories?.[0] || null,
          pricing_type: item.serviceDetails.pricing_type || 'hourly',
          custom_amount: item.serviceDetails.custom_amount || null,
          add_insurance: itemOptions[item.id]?.insurance || false
        })),
        checkout_mode: 'combined'
      };

      const batchResponse = await bookingApi.createBatch(batchData);
      const { batch_id, bookings, total_price } = batchResponse.data;

      // Now initiate payment based on selected method
      if (selectedPaymentMethod === 'paypal') {
        // Create PayPal order for combined amount
        const paypalResponse = await paymentApi.createPayPalBatchOrder({
          booking_ids: bookings.map(b => b.booking_id),
          batch_id: batch_id,
          amount: total_price
        });
        
        // Redirect to PayPal approval URL
        if (paypalResponse.data.approval_url) {
          clearBook();
          window.location.href = paypalResponse.data.approval_url;
        } else {
          throw new Error('No PayPal approval URL received');
        }
      } else {
        // Create Stripe checkout session for combined amount
        const stripeResponse = await paymentApi.createCheckout({
          booking_ids: bookings.map(b => b.booking_id),
          batch_id: batch_id,
          amount: total_price,
          success_url: `${window.location.origin}/bookings?payment=success&batch=${batch_id}`,
          cancel_url: `${window.location.origin}/my-book?payment=cancelled`
        });
        
        if (stripeResponse.data.checkout_url || stripeResponse.data.url) {
          clearBook();
          window.location.href = stripeResponse.data.checkout_url || stripeResponse.data.url;
        } else {
          throw new Error('No Stripe checkout URL received');
        }
      }
    } catch (error) {
      console.error('Payment error:', error);
      toast.error(error.response?.data?.detail || 'Failed to initiate payment. Please try again.');
    } finally {
      setPaymentLoading(false);
    }
  };

  const confirmedCount = bookItems.filter(i => i.status === 'confirmed').length;
  const pendingCount = bookItems.filter(i => i.status !== 'confirmed').length;

  if (bookItems.length === 0) {
    return (
      <div className="min-h-screen pt-20 pb-12" data-testid="my-book-page">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="text-center py-16">
            <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-primary/10 flex items-center justify-center">
              <BookOpen className="h-12 w-12 text-primary" />
            </div>
            <h1 className="text-3xl font-bold mb-4" style={{ fontFamily: 'Syne, sans-serif' }}>
              Your Book is Empty
            </h1>
            <p className="text-muted-foreground mb-6 max-w-md mx-auto">
              Your Book is like a shopping cart for talent. Browse and add Bookees to plan your event or booking.
            </p>
            <Button onClick={() => navigate('/browse')} className="rounded-xl">
              Browse Talent
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 pb-12" data-testid="my-book-page">
      <div className="max-w-5xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3" style={{ fontFamily: 'Syne, sans-serif' }}>
              <BookOpen className="h-8 w-8 text-primary" />
              My Book
            </h1>
            <p className="text-muted-foreground mt-1">
              {bookItems.length} talent{bookItems.length !== 1 ? 's' : ''} • 
              {confirmedCount > 0 && <span className="text-green-500 ml-1">{confirmedCount} confirmed</span>}
              {pendingCount > 0 && <span className="text-yellow-500 ml-1">{pendingCount} pending</span>}
            </p>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={clearBook} className="rounded-xl text-destructive">
              <Trash2 className="h-4 w-4 mr-2" />
              Clear All
            </Button>
          </div>
        </div>

        {/* Info Banner */}
        <Card className="mb-6 bg-primary/5 border-primary/20">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <BookOpen className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="font-medium">How Your Book Works</p>
                <p className="text-sm text-muted-foreground mt-1">
                  1. Add talent to your Book → 2. Message each to confirm availability → 
                  3. Once confirmed, proceed to checkout → 4. Pay together or separately
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Book Items */}
        <div className="space-y-4 mb-8">
          {bookItems.map((item, index) => (
            <Card key={item.id} className="overflow-hidden" data-testid={`book-item-${index}`}>
              <CardContent className="p-0">
                <div className="flex flex-col md:flex-row">
                  {/* Bookee Image */}
                  <div 
                    className="w-full md:w-48 h-48 md:h-auto relative cursor-pointer"
                    onClick={() => navigate(`/talent/${item.bookee.user_id}`)}
                  >
                    <img
                      src={item.bookee.picture || 'https://images.pexels.com/photos/1763069/pexels-photo-1763069.jpeg'}
                      alt={item.bookee.name}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                    <div className="absolute bottom-3 left-3 right-3">
                      <p className="text-white font-bold">{item.bookee.screen_name || item.bookee.name}</p>
                      <div className="flex items-center gap-2 text-white/80 text-sm">
                        <Flame className="h-3 w-3 text-orange-500 fill-current" />
                        <span>{item.bookee.rating?.toFixed(1) || '0.0'}</span>
                        {item.bookee.location && (
                          <>
                            <span>•</span>
                            <MapPin className="h-3 w-3" />
                            <span>{item.bookee.location}</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Details */}
                  <div className="flex-1 p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <Badge variant="secondary" className="mb-2">
                          {item.bookee.main_category || item.bookee.categories?.[0] || 'Service'}
                        </Badge>
                        {getStatusBadge(item.status)}
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeFromBook(item.id)}
                        className="text-destructive hover:text-destructive"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>

                    {/* Service Details */}
                    <div className="grid grid-cols-2 gap-3 text-sm mb-4">
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <CalendarIcon className="h-4 w-4" />
                        <span>{item.serviceDetails.date ? format(new Date(item.serviceDetails.date), 'MMM d, yyyy') : 'Date not set'}</span>
                      </div>
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Clock className="h-4 w-4" />
                        <span>{item.serviceDetails.startTime || 'Time not set'}</span>
                      </div>
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <MapPin className="h-4 w-4" />
                        <span>{item.serviceDetails.eventLocation || 'Location not set'}</span>
                      </div>
                      <div className="flex items-center gap-2 font-medium">
                        <DollarSign className="h-4 w-4 text-primary" />
                        <span>${item.serviceDetails.estimated_price || item.bookee.hourly_rate || 0}/hr est.</span>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2 flex-wrap">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEditItem(item)}
                        className="rounded-lg"
                      >
                        <CalendarIcon className="h-4 w-4 mr-1" />
                        Set Details
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleMessageBookee(item)}
                        className="rounded-lg"
                      >
                        <MessageSquare className="h-4 w-4 mr-1" />
                        Message
                      </Button>
                      {item.status !== 'confirmed' && (
                        <Button
                          size="sm"
                          onClick={() => confirmBookItem(item.id)}
                          className="rounded-lg bg-green-500 hover:bg-green-600"
                        >
                          <CheckCircle className="h-4 w-4 mr-1" />
                          Mark Confirmed
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate(`/talent/${item.bookee.user_id}`)}
                        className="rounded-lg"
                      >
                        <ExternalLink className="h-4 w-4 mr-1" />
                        View Profile
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Checkout Section */}
        <Card className="sticky bottom-4 border-primary/30 bg-background/95 backdrop-blur-sm shadow-lg">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div>
                <p className="text-sm text-muted-foreground">Estimated Total</p>
                <p className="text-2xl font-bold text-primary">${getTotalEstimatedPrice().toFixed(2)}</p>
                <p className="text-xs text-muted-foreground">+ 8% service fee + tax at checkout</p>
              </div>
              
              <div className="flex items-center gap-3">
                <Select value={checkoutMode} onValueChange={setCheckoutMode}>
                  <SelectTrigger className="w-40 rounded-lg">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="combined">Pay Together</SelectItem>
                    <SelectItem value="separate">Pay Separately</SelectItem>
                  </SelectContent>
                </Select>
                
                <Button
                  size="lg"
                  onClick={handleProceedToCheckout}
                  disabled={confirmedCount === 0 || checkoutLoading}
                  className="rounded-xl btn-glow"
                  data-testid="checkout-btn"
                >
                  {checkoutLoading ? (
                    <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                  ) : (
                    <CreditCard className="h-5 w-5 mr-2" />
                  )}
                  {checkoutLoading ? 'Creating...' : `Checkout (${confirmedCount} ready)`}
                </Button>
              </div>
            </div>
            
            {pendingCount > 0 && (
              <p className="text-xs text-yellow-500 mt-2 flex items-center gap-1">
                <AlertCircle className="h-3 w-3" />
                {pendingCount} item{pendingCount !== 1 ? 's' : ''} still pending confirmation
              </p>
            )}
          </CardContent>
        </Card>

        {/* Edit Details Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <CalendarIcon className="h-5 w-5" />
                Set Booking Details
              </DialogTitle>
            </DialogHeader>
            
            {selectedItem && (
              <div className="space-y-4 py-4">
                <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                  <Avatar>
                    <AvatarImage src={selectedItem.bookee.picture} />
                    <AvatarFallback>{selectedItem.bookee.name?.[0]}</AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-medium">{selectedItem.bookee.screen_name || selectedItem.bookee.name}</p>
                    <p className="text-sm text-muted-foreground">{selectedItem.bookee.main_category}</p>
                  </div>
                </div>

                <div className="space-y-3">
                  <div>
                    <Label>Date</Label>
                    <Input
                      type="date"
                      value={selectedItem.serviceDetails.date || ''}
                      onChange={(e) => setSelectedItem({
                        ...selectedItem,
                        serviceDetails: { ...selectedItem.serviceDetails, date: e.target.value }
                      })}
                      className="rounded-lg"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Start Time</Label>
                      <Input
                        type="time"
                        value={selectedItem.serviceDetails.startTime || ''}
                        onChange={(e) => setSelectedItem({
                          ...selectedItem,
                          serviceDetails: { ...selectedItem.serviceDetails, startTime: e.target.value }
                        })}
                        className="rounded-lg"
                      />
                    </div>
                    <div>
                      <Label>End Time</Label>
                      <Input
                        type="time"
                        value={selectedItem.serviceDetails.endTime || ''}
                        onChange={(e) => setSelectedItem({
                          ...selectedItem,
                          serviceDetails: { ...selectedItem.serviceDetails, endTime: e.target.value }
                        })}
                        className="rounded-lg"
                      />
                    </div>
                  </div>

                  <div>
                    <Label>Event Type</Label>
                    <Input
                      placeholder="e.g., Wedding, Birthday Party, Corporate Event"
                      value={selectedItem.serviceDetails.eventType || ''}
                      onChange={(e) => setSelectedItem({
                        ...selectedItem,
                        serviceDetails: { ...selectedItem.serviceDetails, eventType: e.target.value }
                      })}
                      className="rounded-lg"
                    />
                  </div>

                  <div>
                    <Label>Location</Label>
                    <Input
                      placeholder="Event location/address"
                      value={selectedItem.serviceDetails.eventLocation || ''}
                      onChange={(e) => setSelectedItem({
                        ...selectedItem,
                        serviceDetails: { ...selectedItem.serviceDetails, eventLocation: e.target.value }
                      })}
                      className="rounded-lg"
                    />
                  </div>

                  <div>
                    <Label>Notes</Label>
                    <Textarea
                      placeholder="Any special requirements or notes..."
                      value={selectedItem.serviceDetails.notes || ''}
                      onChange={(e) => setSelectedItem({
                        ...selectedItem,
                        serviceDetails: { ...selectedItem.serviceDetails, notes: e.target.value }
                      })}
                      className="rounded-lg"
                    />
                  </div>
                </div>

                <Button onClick={handleSaveDetails} className="w-full rounded-xl">
                  Save Details
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Combined Payment Dialog */}
        <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh]">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <CreditCard className="h-5 w-5 text-primary" />
                Checkout - Pay for All Bookings
              </DialogTitle>
            </DialogHeader>
            
            <ScrollArea className="max-h-[60vh] pr-4">
              <div className="space-y-4 py-4">
                {/* Bookees with individual options */}
                <div className="space-y-3">
                  <p className="text-sm font-medium">Your Bookings:</p>
                  {getCheckoutReadyItems().map((item) => {
                    const prices = calculateItemPrice(item);
                    return (
                      <Card key={item.id} className="border-muted">
                        <CardContent className="p-4">
                          <div className="flex items-start gap-3">
                            <Avatar className="h-12 w-12">
                              <AvatarImage src={item.bookee.picture} />
                              <AvatarFallback>{item.bookee.name?.[0]}</AvatarFallback>
                            </Avatar>
                            <div className="flex-1">
                              <div className="flex items-center justify-between">
                                <div>
                                  <p className="font-medium">{item.bookee.screen_name || item.bookee.name}</p>
                                  <p className="text-xs text-muted-foreground">
                                    {item.serviceDetails.date} at {item.serviceDetails.startTime}
                                  </p>
                                </div>
                                <p className="font-bold text-primary">${prices.total.toFixed(2)}</p>
                              </div>
                              
                              {/* Per-item options */}
                              <div className="mt-3 flex flex-wrap gap-4">
                                <div className="flex items-center gap-2">
                                  <Switch
                                    id={`insurance-${item.id}`}
                                    checked={itemOptions[item.id]?.insurance || false}
                                    onCheckedChange={() => toggleItemOption(item.id, 'insurance')}
                                  />
                                  <Label htmlFor={`insurance-${item.id}`} className="text-xs flex items-center gap-1 cursor-pointer">
                                    <Shield className="h-3 w-3 text-green-500" />
                                    Insurance (+5%)
                                  </Label>
                                </div>
                                <div className="flex items-center gap-2">
                                  <Switch
                                    id={`contract-${item.id}`}
                                    checked={itemOptions[item.id]?.requestContract || false}
                                    onCheckedChange={() => toggleItemOption(item.id, 'requestContract')}
                                  />
                                  <Label htmlFor={`contract-${item.id}`} className="text-xs flex items-center gap-1 cursor-pointer">
                                    <FileText className="h-3 w-3 text-blue-500" />
                                    Request Contract
                                  </Label>
                                </div>
                              </div>
                              
                              {/* Loyalty Discount Toggle - Show when discount is available */}
                              {prices.loyaltyAvailable && (
                                <div className="mt-3 p-3 rounded-lg bg-green-500/10 border border-green-500/30">
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                      <Gift className="h-4 w-4 text-green-500" />
                                      <div>
                                        <p className="text-sm font-medium text-green-500">
                                          Repeat Client Discount Available!
                                        </p>
                                        <p className="text-xs text-muted-foreground">
                                          {prices.availableDiscountPercent}% off as a {prices.loyaltyTier} member
                                        </p>
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <Switch
                                        id={`loyalty-${item.id}`}
                                        checked={itemOptions[item.id]?.applyLoyaltyDiscount || false}
                                        onCheckedChange={() => toggleItemOption(item.id, 'applyLoyaltyDiscount')}
                                      />
                                      <Label htmlFor={`loyalty-${item.id}`} className="text-xs cursor-pointer">
                                        {itemOptions[item.id]?.applyLoyaltyDiscount ? 'Applied' : 'Apply'}
                                      </Label>
                                    </div>
                                  </div>
                                  {prices.discountApplied && (
                                    <p className="text-xs text-green-500 mt-2">
                                      You're saving ${prices.discountAmount.toFixed(2)} on this booking!
                                    </p>
                                  )}
                                </div>
                              )}
                              
                              {/* Price breakdown */}
                              <div className="mt-2 text-xs text-muted-foreground grid grid-cols-2 gap-1">
                                {prices.discountApplied && (
                                  <>
                                    <span className="text-muted-foreground/70 line-through">Original: ${prices.originalPrice.toFixed(2)}</span>
                                    <span className="text-green-500 flex items-center gap-1">
                                      <Gift className="h-3 w-3" />
                                      -{prices.discountPercent}% ({prices.loyaltyTier})
                                    </span>
                                  </>
                                )}
                                <span>Base: ${prices.basePrice.toFixed(2)}</span>
                                <span>Fee (8%): ${prices.platformFee.toFixed(2)}</span>
                                {prices.venueFee > 0 && <span>Venue Fee (10%): ${prices.venueFee.toFixed(2)}</span>}
                                {prices.insurance > 0 && <span>Insurance: ${prices.insurance.toFixed(2)}</span>}
                                <span>Tax: ${prices.tax.toFixed(2)}</span>
                              </div>
                              
                              {/* Loyalty Applied Badge */}
                              {prices.discountApplied && (
                                <div className="mt-2">
                                  <Badge className="bg-green-500/20 text-green-500 border-green-500/30 text-xs">
                                    <Star className="h-3 w-3 mr-1" />
                                    Repeat Client Discount Applied
                                  </Badge>
                                </div>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>

                <Separator />

                {/* Total */}
                <div className="p-4 rounded-xl bg-primary/10 border border-primary/30">
                  <div className="flex items-center justify-between">
                    <span className="font-medium">Total Amount</span>
                    <span className="text-2xl font-bold text-primary">${calculateCheckoutTotal().toFixed(2)}</span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Includes 8% platform fee, 10% venue fee (if applicable), and NY tax (8.875%)
                  </p>
                </div>

                <Separator />

                {/* Insurance Recommendation */}
                <InsuranceRecommendation 
                  onDecline={(declined) => setInsuranceAcknowledged(declined)}
                  showDeclineCheckbox={true}
                  context="checkout"
                />

                <Separator />

                {/* Payment Method Selection */}
                <div className="space-y-3">
                  <p className="text-sm font-medium">Payment Method:</p>
                  <div className="grid grid-cols-2 gap-3">
                    {paymentMethods.map(method => (
                      <Button
                        key={method.id}
                        variant={selectedPaymentMethod === method.id ? "default" : "outline"}
                        className={`rounded-xl h-16 flex flex-col items-center justify-center gap-1 ${
                          selectedPaymentMethod === method.id ? 'ring-2 ring-primary' : ''
                        }`}
                        onClick={() => setSelectedPaymentMethod(method.id)}
                        disabled={!method.available}
                        data-testid={`payment-method-${method.id}`}
                      >
                        {method.id === 'stripe' ? (
                          <CreditCard className="h-5 w-5" />
                        ) : (
                          <PayPalIcon />
                        )}
                        <span className="text-xs">{method.name}</span>
                      </Button>
                    ))}
                  </div>
                </div>
              </div>
            </ScrollArea>

            {/* Action Buttons */}
            <div className="flex gap-3 pt-4 border-t">
              <Button
                variant="outline"
                onClick={() => setPaymentDialogOpen(false)}
                className="flex-1 rounded-xl"
              >
                Cancel
              </Button>
              <Button
                onClick={handleCombinedPayment}
                disabled={paymentLoading || !selectedPaymentMethod || !insuranceAcknowledged}
                className="flex-1 rounded-xl btn-glow"
                data-testid="pay-combined-btn"
              >
                {paymentLoading ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : selectedPaymentMethod === 'paypal' ? (
                  <PayPalIcon />
                ) : (
                  <CreditCard className="h-4 w-4 mr-2" />
                )}
                {paymentLoading ? 'Processing...' : `Pay $${calculateCheckoutTotal().toFixed(2)}`}
              </Button>
            </div>
            {!insuranceAcknowledged && (
              <p className="text-xs text-amber-500 text-center mt-2">
                Please acknowledge the insurance information above to proceed
              </p>
            )}
          </DialogContent>
        </Dialog>

        {/* Checkout Success Dialog (for separate payments) */}
        <Dialog open={checkoutDialogOpen} onOpenChange={setCheckoutDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-green-500">
                <CheckCircle className="h-6 w-6" />
                Bookings Created Successfully!
              </DialogTitle>
            </DialogHeader>
            
            {createdBookings && (
              <div className="space-y-4 py-4">
                <div className="p-4 rounded-xl bg-green-500/10 border border-green-500/30">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-muted-foreground">Total Bookings</span>
                    <span className="font-bold">{createdBookings.total_count}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Total Amount</span>
                    <span className="text-xl font-bold text-primary">${createdBookings.total_price?.toFixed(2)}</span>
                  </div>
                </div>

                <div className="space-y-2">
                  <p className="text-sm font-medium">Created Bookings:</p>
                  {createdBookings.bookings?.map((booking) => (
                    <div key={booking.booking_id} className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                      <div>
                        <p className="font-medium text-sm">{booking.service_name}</p>
                        <p className="text-xs text-muted-foreground">
                          {format(new Date(booking.start_time), 'MMM d, yyyy h:mm a')}
                        </p>
                      </div>
                      <span className="font-bold text-primary">${booking.total_price?.toFixed(2)}</span>
                    </div>
                  ))}
                </div>

                <Separator />

                <Button
                  onClick={() => {
                    setCheckoutDialogOpen(false);
                    clearBook();
                    navigate('/bookings');
                  }}
                  className="w-full rounded-xl btn-glow"
                >
                  <Wallet className="h-4 w-4 mr-2" />
                  Go to Bookings to Pay
                </Button>

                <p className="text-xs text-center text-muted-foreground">
                  Pay for each booking individually on the Bookings page
                </p>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default MyBook;
